<script setup lang="ts">
import type { FilePath, UserInfoUpsert } from '~/types'
import { CustomerLanguage } from '~/prisma/enums'

const props = defineProps<{ payload: UserInfoUpsert, isPreview?: boolean }>()
const emit = defineEmits<{ (e: 'save', payload: UserInfoUpsert): void }>()

interface PayloadData extends Omit<UserInfoUpsert, 'userPhoto'> {
  // Since FormKitFileDropzone returns an array of files
  userPhoto?: FilePath[]
}

const { cloned } = useCloned<PayloadData>({
  ...props.payload,
  userPhoto: props.payload.userPhoto ? [props.payload.userPhoto] : undefined,
})

function save(input: PayloadData) {
  const userInfo = {
    ...input,
    userPhoto: input.userPhoto && input.userPhoto.length > 0 ? input.userPhoto[0] : null,
  }
  emit('save', userInfo)
}

const languageOptions = useTranslatedSelectOptions('customer.language', Object.values(CustomerLanguage))
const clonedForPreview = computed(() => {
  const { userPhoto, ...rest } = cloned.value
  return {
    ...rest,
    userPhoto: userPhoto ? userPhoto[0] : null,
  }
})
</script>

<template>
  <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
  <FormKit v-model="cloned" type="form" :submit-label="$t('button.save')" @submit="save($event)">
    <TheDevOnlyNiceJson :input="payload" :current-value="cloned" />
    <div class="flex items-center justify-center mb-2">
      <UserInfoCard :user-info="clonedForPreview" is-preview />
    </div>
    <div class="flex space-x-2">
      <FormKit
        name="function"
        type="text"
        :label="$t('userInfo.field.function.name')"
      />

      <div class="w-full">
        <p class="font-semibold mb-1">
          {{ $t('userInfo.field.hasLanguageProficiencyIn.name') }}
        </p>
        <n-select
          v-model:value="cloned.hasLanguageProficiencyIn"
          size="large"
          :options="languageOptions"
          :consistent-menu-width="false"
          filterable
          clearable
          multiple
        />
      </div>
    </div>

    <div class="flex space-x-2">
      <FormKit
        name="phone"
        type="text"
        :label="$t('userInfo.field.phone.name')"
      />
      <FormKit
        name="mobilePhone"
        type="text"
        :label="$t('userInfo.field.mobilePhone.name')"
      />
    </div>

    <FormKitFileDropzone
      name="userPhoto"
      :label="$t('userInfo.field.userPhoto.name')"
      accept="image/png,image/jpg,image/jpeg"
      :validation="[['max', 1]]"
    />
  </FormKit>
</template>
